import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckboxRow from "./CheckboxRow";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { SELECTED_COLOR } from "../../../../../constants/colors";

Prefectures.propTypes = {
  prefByArea: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        name: PropTypes.string.isRequired,
      })
    )
  ).isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])).isRequired,
  setSelectedIds: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
};

export default function Prefectures({ prefByArea, selectedIds, setSelectedIds, isOpen = true }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // 複数のエリアを開いたままにするため、expandedAreas を配列で持つ
  const [expandedAreas, setExpandedAreas] = useState([]);

  // prefectureId -> area を引けるMapを作る（高速＆読みやすい）
  const areaNameByPrefectureId = useMemo(() => {
    const map = new Map();
    Object.entries(prefByArea).forEach(([areaName, prefectures]) => {
      prefectures.forEach((prefecture) => {
        map.set(prefecture.id, areaName);
      });
    });
    return map;
  }, [prefByArea]);

  const toggleAreaExpanded = useCallback((areaName) => {
    setExpandedAreas((previousExpandedAreas) => {
      const isExpanded = previousExpandedAreas.includes(areaName);
      if (isExpanded) {
        return previousExpandedAreas.filter((name) => name !== areaName);
      }
      return [...previousExpandedAreas, areaName];
    });
  }, []);

  const togglePrefecture = useCallback(
    (prefectureId) => (event) => {
      const isChecked = event.target.checked;

      const nextSelectedIds = isChecked
        ? [...selectedIds, prefectureId]
        : selectedIds.filter((existingId) => existingId !== prefectureId);

      setSelectedIds(nextSelectedIds);

      // チェックした都道府県が属するエリアを開いたままにする
      if (isChecked) {
        const areaName = areaNameByPrefectureId.get(prefectureId);
        if (areaName) {
          setExpandedAreas((previousExpandedAreas) => {
            if (previousExpandedAreas.includes(areaName)) return previousExpandedAreas;
            return [...previousExpandedAreas, areaName];
          });
        }
      }
    },
    [selectedIds, setSelectedIds, areaNameByPrefectureId],
  );

  // 選択された都道府県が属するエリアは自動で開く。
  // 選択解除してもこの場では自動で閉じない（ユーザー操作中に瞬時に閉じてしまうのを防ぐため）。
  // 選択が空の状態でタブ/モーダルを閉じて再度開くと、コンポーネント再マウントにより初期状態（閉じた状態）に戻る。
  const previousIsOpenRef = useRef(false);

useEffect(() => {
  const previousIsOpen = previousIsOpenRef.current;
  previousIsOpenRef.current = isOpen;

  // 閉じている間は何もしない
  if (!isOpen) return;

  const areaNamesToOpen = new Set();
  selectedIds.forEach((prefectureId) => {
    const areaName = areaNameByPrefectureId.get(prefectureId);
    if (areaName) areaNamesToOpen.add(areaName);
  });

  setExpandedAreas((previousExpandedAreas) => {
    // ★閉→開の瞬間だけ「選択中のエリアだけ」にリセット（未選択は閉じる）
    if (!previousIsOpen) {
      return Array.from(areaNamesToOpen);
    }

    // ★開いている間は「開く」だけ（閉じない）
    const merged = new Set([...previousExpandedAreas, ...areaNamesToOpen]);
    return Array.from(merged);
  });
}, [isOpen, selectedIds, prefByArea]);

  return (
    <Box>
      {Object.entries(prefByArea).map(([areaName, prefectures]) => (
        <Accordion
          key={areaName}
          expanded={expandedAreas.includes(areaName)}
          onChange={() => toggleAreaExpanded(areaName)}
          disableGutters
          elevation={0}
          sx={{
            borderBottom: "1px solid #333",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700, color: SELECTED_COLOR }}>
              {areaName}
            </Typography>
          </AccordionSummary>

          <AccordionDetails sx={{ pt: 0 }}>
            <Box
              sx={{
                ...(!isMobile && {
                  display: "grid",
                  gridTemplateColumns: "repeat(5, 1fr)",
                  columnGap: 2,
                  rowGap: 1,
                  color: SELECTED_COLOR,
                }),
              }}
            >
              {prefectures.map((prefecture) => (
                <CheckboxRow
                  key={prefecture.id}
                  label={prefecture.name}
                  checked={selectedIds.includes(prefecture.id)}
                  onChange={togglePrefecture(prefecture.id)}
                />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}